import {
  Hub,
  Sensor,
  SensorEvent,
  useHubs,
  useSensors,
  useSensorEvents,
} from "common";
import { initializeAmplify } from "common/src/utils/amplifyBootstrap";
import {
  createContext,
  useContext,
  ReactNode,
  useEffect,
  useState,
} from "react";

export interface HubWithLocation extends Hub {
  location?: string;
}

interface AppSyncContextType {
  hub: Hub | null;
  hubs: HubWithLocation[];
  hubsLoading: boolean;
  hubsError: Error | null;
  fetchHubs: () => Promise<void>;
  getHubById: (id: string) => Promise<Hub>;
  addHub: (input: any) => Promise<Hub>;
  modifyHub: (input: any) => Promise<Hub>;
  removeHub: (id: string) => Promise<Hub>;

  sensors: Sensor[];
  sensorsLoading: boolean;
  sensorsError: Error | null;
  fetchSensorsByHub: (hubId: string) => Promise<void>;
  getSensorById: (id: string) => Promise<Sensor>;
  addSensor: (input: any) => Promise<Sensor>;
  updateSensor: (input: any) => Promise<Sensor>;
  removeSensor: (id: string) => Promise<Sensor>;

  sensorEvents: SensorEvent[];
  sensorEventsLoading: boolean;
  sensorEventsError: Error | null;
  fetchSensorEventsBySensor: (
    sensorId: string,
    limit?: number,
  ) => Promise<void>;
  fetchSensorEventsByHub: (hubId: string, limit?: number) => Promise<void>;
  addSensorEvent: (input: any) => Promise<SensorEvent>;

  selectedHub: HubWithLocation | null;
  selectHubById: (id: string) => void;
  selectedSensorId: string | null;
  setSelectedSensorId: (id: string | null) => void;

  fetchSensorHistoricalState: (
    sensorId: string,
    startTime: number,
    endTime: number,
    rollup: number,
  ) => Promise<any>;
}

const AppSyncContext = createContext<AppSyncContextType | undefined>(undefined);

export const AppSyncProvider = ({ children }: { children: ReactNode }) => {
  const [selectedHub, setSelectedHub] = useState<HubWithLocation | null>(null);
  const [selectedSensorId, setSelectedSensorId] = useState<string | null>(null);

  // Ensure Amplify is initialized before using any GraphQL hooks
  useEffect(() => {
    // Initialize Amplify to ensure proper configuration
    initializeAmplify();
  }, []);

  const {
    hub,
    hubs,
    loading: hubsLoading,
    error: hubsError,
    fetchHubs,
    getHubById,
    addHub,
    modifyHub,
    removeHub,
  } = useHubs({ fetchAndSubscribeOnLoad: true });

  const {
    sensors,
    loading: sensorsLoading,
    error: sensorsError,
    fetchSensorsByHub,
    getSensorById,
    addSensor,
    updateSensor,
    removeSensor,
  } = useSensors(selectedHub?.id);

  const {
    sensorEvents,
    loading: sensorEventsLoading,
    error: sensorEventsError,
    fetchSensorEventsBySensor,
    fetchSensorEventsByHub,
    addSensorEvent,
    fetchSensorHistoricalState,
  } = useSensorEvents(selectedHub?.id, selectedSensorId || undefined);

  const hubsWithLocation = hubs.map((hub) => ({
    ...hub,
    location:
      hub.street && hub.city
        ? `${hub.street}, ${hub.city}${hub.state ? `, ${hub.state}` : ""}${hub.zip ? ` ${hub.zip}` : ""}`
        : hub.city && hub.state
          ? `${hub.city}, ${hub.state}`
          : "Location Unknown",
  }));

  const selectHubById = (id: string) => {
    const hub = hubsWithLocation.find((hub) => hub.id === id);
    if (hub) {
      setSelectedHub(hub);
      setSelectedSensorId(null);
    } else {
      console.warn(`Hub with id ${id} not found`);
    }
  };

  const value: AppSyncContextType = {
    hub,
    hubs: hubsWithLocation,
    hubsLoading,
    hubsError,
    fetchHubs,
    getHubById,
    addHub,
    modifyHub,
    removeHub,

    sensors,
    sensorsLoading: sensorsLoading || false,
    sensorsError,
    fetchSensorsByHub,
    getSensorById,
    addSensor,
    updateSensor,
    removeSensor,

    sensorEvents,
    sensorEventsLoading,
    sensorEventsError,
    fetchSensorEventsBySensor,
    fetchSensorEventsByHub,
    addSensorEvent,

    selectedHub,
    selectHubById,
    selectedSensorId,
    setSelectedSensorId,

    fetchSensorHistoricalState,
  };

  useEffect(() => {
    if (hubs.length > 0 && !selectedHub) {
      selectHubById(hubs[0].id);
    }
  }, [hubs.length]);

  return (
    <AppSyncContext.Provider value={value}>{children}</AppSyncContext.Provider>
  );
};

export const useAppSync = () => {
  const context = useContext(AppSyncContext);
  if (context === undefined) {
    throw new Error("useAppSync must be used within an AppSyncProvider");
  }
  return context;
};
